import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Custom hook for global keyboard shortcuts
 * Provides power user productivity features
 */
export const useKeyboardShortcuts = (options = {}) => {
    const navigate = useNavigate();
    const {
        onShowShortcuts = null,
        disabled = false
    } = options;

    useEffect(() => {
        if (disabled) return;

        const handleKeyPress = (e) => {
            // Don't trigger shortcuts when typing in inputs
            if (
                e.target.tagName === 'INPUT' ||
                e.target.tagName === 'TEXTAREA' ||
                e.target.isContentEditable
            ) {
                // Exception: Esc should still work to blur inputs
                if (e.key === 'Escape') {
                    e.target.blur();
                }
                return;
            }

            const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
            const modKey = isMac ? e.metaKey : e.ctrlKey;

            // Ctrl/Cmd + N - New session
            if (modKey && e.key === 'n') {
                e.preventDefault();
                navigate('/new-session');
            }

            // Ctrl/Cmd + K - Focus search
            if (modKey && e.key === 'k') {
                e.preventDefault();
                const searchInput = document.querySelector('[data-search-input]');
                if (searchInput) {
                    searchInput.focus();
                }
            }

            // Ctrl/Cmd + / - Show shortcuts help
            if (modKey && e.key === '/') {
                e.preventDefault();
                if (onShowShortcuts) {
                    onShowShortcuts();
                }
            }

            // Ctrl/Cmd + S - Save (prevent browser default)
            if (modKey && e.key === 's') {
                e.preventDefault();
                // Trigger form submit if in a form
                const form = document.querySelector('form');
                if (form) {
                    form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
                }
            }

            // Esc - Close modals/dialogs
            if (e.key === 'Escape') {
                // Find and close any open modals
                const closeButtons = document.querySelectorAll('[data-modal-close]');
                if (closeButtons.length > 0) {
                    closeButtons[closeButtons.length - 1].click();
                }
            }

            // G then D - Go to Dashboard (Gmail-style navigation)
            if (e.key === 'g') {
                const handleSecondKey = (e2) => {
                    if (e2.key === 'd') {
                        navigate('/dashboard');
                    } else if (e2.key === 's') {
                        navigate('/sessions');
                    } else if (e2.key === 'p') {
                        navigate('/profile');
                    }
                    window.removeEventListener('keydown', handleSecondKey);
                };
                window.addEventListener('keydown', handleSecondKey);
                setTimeout(() => window.removeEventListener('keydown', handleSecondKey), 1000);
            }
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [navigate, onShowShortcuts, disabled]);
};

/**
 * List of all available keyboard shortcuts
 */
export const KEYBOARD_SHORTCUTS = [
    {
        category: 'Navigation',
        shortcuts: [
            { keys: ['Ctrl/Cmd', 'N'], description: 'Start new session' },
            { keys: ['G', 'D'], description: 'Go to Dashboard' },
            { keys: ['G', 'S'], description: 'Go to Sessions' },
            { keys: ['G', 'P'], description: 'Go to Profile' },
        ]
    },
    {
        category: 'Actions',
        shortcuts: [
            { keys: ['Ctrl/Cmd', 'S'], description: 'Save/Submit form' },
            { keys: ['Ctrl/Cmd', 'K'], description: 'Focus search' },
            { keys: ['Esc'], description: 'Close modal/blur input' },
        ]
    },
    {
        category: 'Help',
        shortcuts: [
            { keys: ['Ctrl/Cmd', '/'], description: 'Show keyboard shortcuts' },
        ]
    }
];
